import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { isEmpty } from 'lodash';

import { sendClickEvent } from '@/utils/AnalyticsUtils';
import { dayjs, getDateDifference } from '@/utils/dateTimeUtils';
import {
	HOME_DATE_FILTER_CLOSED,
	HOME_DATE_FILTER_OPENED,
	HOME_DATE_SELECTED,
	TRANSACTION_DATE_FILTER_CLOSED,
	TRANSACTION_DATE_FILTER_OPENED,
	TRANSACTION_DATE_SELECTED,
	HOME_DATE_FILTER_CLICKED,
	TRANSACTION_DATE_FILTER_CLICKED,
	PAYLINK_DATE_FILTER_OPENED,
	PAYLINK_DATE_FILTER_CLOSED,
	PAYLINK_DATE_FILTER_CLICKED,
	PAYLINK_DATE_SELECTED,
} from '@/constants';
import { Modal, Option, Select } from '@/widgets';
import { CalendarIcon } from '@/assets/icons';
import { TEST_IDS } from '@/constants/testConstants';

import styles from './DateRangeSelectGroup.module.scss';
import CustomDateRangePicker from './CustomDateRangePicker';

const singularDateTypes = ['yesterday', 'today'];
const { DATE_RANGE_DATE_TYPE_SELECTOR, DATE_RANGE_DISPLAYED_DATE } = TEST_IDS;
const defaultDateTypes = [
	{ key: 'past6months', displayName: 'Past 6 Months' },
	{ key: 'past3months', displayName: 'Past 3 Months' },
	{ key: 'month', displayName: 'This Month' },
	{ key: 'week', displayName: 'This Week' },
	{ key: 'yesterday', displayName: 'Yesterday' },
	{ key: 'today', displayName: 'Today' },
	{ key: 'custom', displayName: 'Custom' },
];

const containerEventMap = {
	home: {
		DATE_FILTER_OPENED: HOME_DATE_FILTER_OPENED,
		DATE_FILTER_CLOSED: HOME_DATE_FILTER_CLOSED,
		DATE_FILTER_CLICKED: HOME_DATE_FILTER_CLICKED,
		DATE_SELECTED: HOME_DATE_SELECTED,
	},
	transactionHistory: {
		DATE_FILTER_OPENED: TRANSACTION_DATE_FILTER_OPENED,
		DATE_FILTER_CLOSED: TRANSACTION_DATE_FILTER_CLOSED,
		DATE_FILTER_CLICKED: TRANSACTION_DATE_FILTER_CLICKED,
		DATE_SELECTED: TRANSACTION_DATE_SELECTED,
	},
	onlinePaylinks: {
		DATE_FILTER_OPENED: PAYLINK_DATE_FILTER_OPENED,
		DATE_FILTER_CLOSED: PAYLINK_DATE_FILTER_CLOSED,
		DATE_FILTER_CLICKED: PAYLINK_DATE_FILTER_CLICKED,
		DATE_SELECTED: PAYLINK_DATE_SELECTED,
	},
};

const DateRangeSelectGroup = (props) => {
	const dispatch = useDispatch();
	const {
		container = '',
		updateState = () => {},
		showTimePicker = true,
		disabledDateTypesKeys = [],
		className,
		loading = false,
		selectedDateRange,
		customActiveDateRange,
	} = props;

	const [localSelectedDateRange, setLocalSelectedDateRange] =
		useState(selectedDateRange);
	const [enabledDateTypes, setEnabledDateTypes] = useState(defaultDateTypes);
	const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

	useEffect(() => {
		if (isEmpty(disabledDateTypesKeys)) return;

		const _filteredDateTypes = [...defaultDateTypes].filter(
			(dateType) => !disabledDateTypesKeys.includes(dateType.key),
		);
		setEnabledDateTypes(_filteredDateTypes);
	}, [disabledDateTypesKeys]);

	const getContainerWiseEventName = (eventName) => {
		const containerEvents = containerEventMap[container];
		return containerEvents?.[eventName] || eventName;
	};

	const getDateRangeByType = (dateType) => {
		let fromDate = null;
		let toDate = dayjs().valueOf();
		switch (dateType) {
			case 'past6months':
				fromDate = dayjs().subtract(6, 'months').startOf('day').valueOf();
				break;
			case 'past3months':
				fromDate = dayjs().subtract(3, 'months').startOf('day').valueOf();
				break;
			case 'month':
				fromDate = dayjs().startOf('month').valueOf();
				break;
			case 'yesterday':
				fromDate = dayjs().subtract(1, 'days').startOf('day').valueOf();
				toDate = dayjs().subtract(1, 'days').endOf('day').valueOf();
				break;
			case 'today':
				fromDate = dayjs().startOf('day').valueOf();
				break;
			case 'week':
			default:
				fromDate = dayjs().startOf('isoWeek').valueOf();
		}
		return { fromDate, toDate };
	};

	const onDateRangeSelect = (dateRange) => {
		const _selectedDateType = {
			from: dayjs(dateRange.fromDate).valueOf(),
			to: dayjs(dateRange.toDate).valueOf(),
			dateType: dateRange.selectedDateType,
		};
		setLocalSelectedDateRange(_selectedDateType);
		dispatch(updateState(_selectedDateType));
		const eventName = getContainerWiseEventName('DATE_SELECTED');
		let eventValue = 'NA';
		if (_selectedDateType.dateType === 'custom') {
			eventValue = getDateDifference(
				_selectedDateType.from,
				_selectedDateType.to,
				'days',
			);
		}
		if (eventName)
			sendClickEvent({
				name: eventName,
				value: `${eventValue}`,
				additionalData: {
					date_type: _selectedDateType.dateType,
				},
			});
		setShowCustomDatePicker(false);
	};

	const handleDateTypeClick = (selectedDateType) => {
		if (selectedDateType !== 'custom') {
			setShowCustomDatePicker(false);
			onDateRangeSelect({
				...getDateRangeByType(selectedDateType),
				selectedDateType,
			});
			return;
		}
		if (!showCustomDatePicker) {
			setLocalSelectedDateRange({
				dateType: selectedDateType,
			});
			setShowCustomDatePicker(true);
		}
	};

	const onCancelClick = () => {
		setLocalSelectedDateRange(selectedDateRange);
		setShowCustomDatePicker(false);
		const eventName = getContainerWiseEventName('DATE_FILTER_CLOSED');
		if (eventName) sendClickEvent({ name: eventName });
	};

	const handleSelectClicked = () => {
		const eventName = getContainerWiseEventName('DATE_FILTER_CLICKED');
		if (eventName) sendClickEvent({ name: eventName });
	};

	const handleCustomOptionClicked = () => {
		if (!showCustomDatePicker && !loading) {
			setShowCustomDatePicker(true);
			const eventName = getContainerWiseEventName('DATE_FILTER_OPENED');
			if (eventName) sendClickEvent({ name: eventName });
		}
	};

	return (
		<>
			<div
				className={cx(styles.selectGroup, className)}
				data-id={`${TEST_IDS.DATE_RANGE}`}
			>
				<div data-id={DATE_RANGE_DATE_TYPE_SELECTOR}>
					<Select
						disabled={loading}
						onChange={handleDateTypeClick}
						prefixIcon={<CalendarIcon className={styles.calendarIcon} />}
						onClick={handleSelectClicked}
						defaultValue={selectedDateRange.dateType}
						value={localSelectedDateRange.dateType}
						bordered={false}
						optionLabelProp="label"
						classes={{ selectWrapper: styles.selectWrapper }}
					>
						{enabledDateTypes.map((_dateType) => (
							<Option
								key={_dateType.key}
								className={cx(styles.option, {
									[styles.selected]:
										localSelectedDateRange.dateType === _dateType.key,
								})}
								value={_dateType.key}
								label={_dateType.displayName}
							>
								<div
									onClick={
										_dateType.key === 'custom'
											? handleCustomOptionClicked
											: null
									}
									data-id={_dateType.key}
								>
									{_dateType.displayName}
								</div>
							</Option>
						))}
					</Select>
				</div>

				<div
					onClick={handleCustomOptionClicked}
					className={styles.dateTextWrapper}
					data-id={DATE_RANGE_DISPLAYED_DATE}
				>
					{dayjs(selectedDateRange.from).format('DD MMM YY')}
					{!singularDateTypes.includes(selectedDateRange.dateType) && (
						<> - {dayjs(selectedDateRange.to).format('DD MMM YY')}</>
					)}
				</div>
			</div>

			<Modal
				className={styles.modal}
				footer={null}
				open={showCustomDatePicker}
				maskClosable={false}
				closable
				destroyOnClose
				onCancel={onCancelClick}
				title="Select Custom Date Range"
			>
				<CustomDateRangePicker
					activeDateRange={customActiveDateRange}
					open={showCustomDatePicker}
					showTimePicker={showTimePicker}
					startDate={selectedDateRange.from}
					endDate={selectedDateRange.to}
					onApplyClick={onDateRangeSelect}
					onCancelClick={onCancelClick}
					container={container}
				/>
			</Modal>
		</>
	);
};

export default DateRangeSelectGroup;
